import { isNull, reduce } from 'lodash';
import titleCase, { capitalizeName, withTitleCasedDrugFields } from 'common/utils/titleCase';
import graphqlClient from '../graphql';
import {
  addManufacturerGql,
  addVendorGql,
  bulkLockItemsForPhysicalCountGql,
  bulkUnlockItemsFromPhysicalCountGql,
  createCountGql,
  createInventoryGql,
  createInventoryReceivingLineSplitsGql,
  deleteManufacturerGql,
  discardAllocationOfLineItemsGql,
  inventoryAlertSummaryGql,
  inventoryLotsByLotIdGql,
  lineItemActiveAllocationsGql,
  listAvailableLotsForDispenseGql,
  listCountsGql,
  listExpiringSoonItemsGql,
  listInPhysicalCountItemsGql,
  listInventoryByNdcGql,
  listInventoryItemsGql,
  listInventoryManufacturersGql,
  listInventoryReceivingLineSplitsGql,
  listInventoryReceivingsGql,
  listInventoryTransactionsGql,
  listLockedInventoryItemsGql,
  listLotAllocationsGql,
  listLowDaysOfSupplyItemsGql,
  listLowStockItemsGql,
  listManufacturersGql,
  listPartnerNdcPreferenceCountsGql,
  listPartnerNdcPreferencesGql,
  listProductNdcsGql,
  listRecordableLotsForProductGql,
  listVendorsGql,
  lockItemForPhysicalCountGql,
  lotSummaryGql,
  manualInventoryAdjustGql,
  recallLotAllocationsToInventoryGql,
  removePartnerNdcPreferenceGql,
  resolveLotAllocationsGql,
  setDefaultNdcForProductGql,
  setPartnerNdcPreferenceGql,
  unlockItemFromPhysicalCountGql,
  updateInventoryReceivingLineSplitQuantityGql,
  updateInventoryReorderConfigGql,
  updateManufacturerGql,
  upsertInventoryReceivingGql,
  verifyInventoryReceivingLineSplitGql,
} from './gql';

const buildFilter = (filter) =>
  reduce(
    filter,
    (result, value, key) => ({
      ...result,
      [key]: isNull(value) ? undefined : value,
    }),
    {}
  );

export const listInventoryReceivingsApi = ({ filter, page }) =>
  graphqlClient
    .request(listInventoryReceivingsGql, {
      offset: (page - 1) * 25,
      filter: buildFilter(filter),
    })
    .then(({ inventoryReceivings }) => inventoryReceivings);

export const upsertInventoryReceivingApi = (input) =>
  graphqlClient
    .request(upsertInventoryReceivingGql, { input })
    .then(({ upsertInventoryReceiving }) => upsertInventoryReceiving.receiving);

export const listInventoryItemsApi = ({ filter }) =>
  graphqlClient
    .request(listInventoryItemsGql, { filter: buildFilter(filter) })
    .then(({ listInventoryItems }) => (listInventoryItems ?? []).map(withTitleCasedDrugFields));

// includeInactive also returns deactivated NDCs (the full inventory master).
export const listInventoryByNdcApi = (ndc, { includeInactive = false } = {}) =>
  graphqlClient
    .request(listInventoryByNdcGql, { ndc, includeInactive })
    .then(({ listInventoryByNdc }) => (listInventoryByNdc ?? []).map(withTitleCasedDrugFields));

export const listProductNdcsApi = (productId) =>
  graphqlClient
    .request(listProductNdcsGql, { productId })
    .then(({ listProductNdcs }) => (listProductNdcs ?? []).map(withTitleCasedDrugFields));

export const listInventoryManufacturersApi = () =>
  graphqlClient
    .request(listInventoryManufacturersGql)
    .then(({ listInventoryManufacturers }) => listInventoryManufacturers ?? []);

export const lotSummaryApi = ({ inventoryItemId, ndc, fulfillingPharmacyId, filter }) =>
  graphqlClient
    .request(lotSummaryGql, { inventoryItemId, ndc, fulfillingPharmacyId, filter })
    .then(({ lotSummary }) => lotSummary);

export const inventoryLotsByLotIdApi = ({ lotId }) =>
  graphqlClient
    .request(inventoryLotsByLotIdGql, { lotId })
    .then(({ inventoryLotsByLotId }) => inventoryLotsByLotId ?? []);

export const listInventoryReceivingLineSplitsApi = ({ receivingLineId }) =>
  graphqlClient
    .request(listInventoryReceivingLineSplitsGql, {
      filter: { receivingLineId },
      limit: 100,
      offset: 0,
    })
    .then(({ inventoryReceivingLineSplits }) => inventoryReceivingLineSplits);

export const createInventoryReceivingLineSplitsApi = (input) =>
  graphqlClient
    .request(createInventoryReceivingLineSplitsGql, { input })
    .then(({ createInventoryReceivingLineSplits }) => createInventoryReceivingLineSplits.splits);

export const verifyInventoryReceivingLineSplitApi = (input) =>
  graphqlClient
    .request(verifyInventoryReceivingLineSplitGql, { input })
    .then(({ verifyInventoryReceivingLineSplit }) => verifyInventoryReceivingLineSplit.split);

const toTitleCase = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

const PAGE_SIZE = 100;

export const listVendorsApi = ({ vendor, sort, page = 1 } = {}) =>
  graphqlClient
    .request(listVendorsGql, {
      filter: vendor ? { vendor } : undefined,
      sort: sort || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
    .then(({ listVendors }) => ({
      items: (listVendors.items ?? []).map((v) => ({ ...v, vendor: toTitleCase(v.vendor) })),
      totalCount: listVendors.totalCount,
    }));

export const addVendorApi = (vendor) =>
  graphqlClient.request(addVendorGql, { input: { vendor } }).then(({ addVendor }) => ({
    ...addVendor.vendor,
    vendor: toTitleCase(addVendor.vendor.vendor),
  }));

// POM-6405 — Inventory → Manufacturer Management. Names are shown exactly as
// stored (no title-casing): that string is what the Add-NDC dropdown offers and
// what gets saved on new inventory items.
export const MANUFACTURER_PAGE_SIZE = 25;

// status: 'ACTIVE' | 'INACTIVE' | undefined (both).
export const listManufacturersApi = ({ manufacturer, status, sort, page = 1 } = {}) => {
  const filter = {};
  if (manufacturer) filter.manufacturer = manufacturer;
  if (status === 'ACTIVE') filter.isDeleted = false;
  if (status === 'INACTIVE') filter.isDeleted = true;

  return graphqlClient
    .request(listManufacturersGql, {
      filter,
      sort: sort || undefined,
      limit: MANUFACTURER_PAGE_SIZE,
      offset: (page - 1) * MANUFACTURER_PAGE_SIZE,
    })
    .then(({ listManufacturers }) => ({
      items: listManufacturers.items ?? [],
      totalCount: listManufacturers.totalCount,
    }));
};

export const addManufacturerApi = (manufacturer) =>
  graphqlClient
    .request(addManufacturerGql, { input: { manufacturer } })
    .then(({ addManufacturer }) => addManufacturer.manufacturer);

export const updateManufacturerApi = ({ manufacturerId, manufacturer }) =>
  graphqlClient
    .request(updateManufacturerGql, { input: { manufacturerId, manufacturer } })
    .then(({ updateManufacturer }) => updateManufacturer.manufacturer);

export const deleteManufacturerApi = (manufacturerId) =>
  graphqlClient
    .request(deleteManufacturerGql, { input: { manufacturerId } })
    .then(({ deleteManufacturer }) => deleteManufacturer.manufacturer);

export const updateInventoryReceivingLineSplitQuantityApi = (input) =>
  graphqlClient
    .request(updateInventoryReceivingLineSplitQuantityGql, { input })
    .then(
      ({ updateInventoryReceivingLineSplitQuantity }) =>
        updateInventoryReceivingLineSplitQuantity.split
    );

export const listAvailableLotsForDispenseApi = ({ productId, fulfillingPharmacyId, filter }) =>
  graphqlClient
    .request(listAvailableLotsForDispenseGql, { productId, fulfillingPharmacyId, filter })
    .then(({ listAvailableLotsForDispense }) => {
      const { groups = [], totalCount, isLocked } = listAvailableLotsForDispense;
      // The backend groups lots by NDC (a product resolves to multiple NDCs). The
      // popup renders a single FEFO list, so flatten the groups into lot rows,
      // carrying each group's NDC metadata onto every lot.
      const items = groups.flatMap((group) =>
        group.lots.map((lot) => ({
          ...lot,
          ndc: group.ndc,
          manufacturer: group.manufacturer,
          drugName: group.drugName,
          isDefault: group.isDefault,
        }))
      );
      return { items, groups, totalCount, isLocked };
    });

// POM-6083 - backs the "Record a lot manually" panel in Fill Product. Returns the
// closed NDC list for the product and the zero-quantity lots the picker hides, so the
// operator can claim the real row instead of typing a lot number that may create a
// duplicate row for one physical lot.
export const listRecordableLotsForProductApi = ({ productId, fulfillingPharmacyId }) =>
  graphqlClient
    .request(listRecordableLotsForProductGql, { productId, fulfillingPharmacyId })
    .then(({ listRecordableLotsForProduct }) => {
      const {
        ndcOptions = [],
        depletedLots = [],
        hasInventoryMaster = false,
      } = listRecordableLotsForProduct ?? {};
      return { ndcOptions, depletedLots, hasInventoryMaster };
    });

export const listInventoryTransactionsApi = ({
  ndc,
  fulfillingPharmacyId,
  limit = 100,
  offset = 0,
  filter,
}) =>
  graphqlClient
    .request(listInventoryTransactionsGql, { ndc, fulfillingPharmacyId, limit, offset, filter })
    .then(({ listInventoryTransactions }) => listInventoryTransactions);

export const listLotAllocationsApi = ({ filter, sort, limit = 20, offset = 0 } = {}) =>
  graphqlClient
    .request(listLotAllocationsGql, {
      filter: filter ? buildFilter(filter) : undefined,
      sort,
      limit,
      offset,
    })
    .then(({ listLotAllocations }) => ({
      ...listLotAllocations,
      items: (listLotAllocations.items ?? []).map(withTitleCasedDrugFields),
    }));

export const resolveLotAllocationsApi = (input) =>
  graphqlClient
    .request(resolveLotAllocationsGql, { input })
    .then(({ resolveLotAllocations }) => resolveLotAllocations);

export const lineItemActiveAllocationsApi = (lineItemId) =>
  graphqlClient
    .request(lineItemActiveAllocationsGql, { lineItemId })
    .then(({ lineItemActiveAllocations }) =>
      (lineItemActiveAllocations ?? []).map(withTitleCasedDrugFields)
    );

export const discardAllocationOfLineItemsApi = (lineItemId) =>
  graphqlClient
    .request(discardAllocationOfLineItemsGql, { lineItemId })
    .then(({ discardAllocationOfLineItems }) => discardAllocationOfLineItems);

export const recallLotAllocationsToInventoryApi = (input) =>
  graphqlClient
    .request(recallLotAllocationsToInventoryGql, { input })
    .then(({ recallLotAllocationsToInventory }) => recallLotAllocationsToInventory);

export const manualInventoryAdjustApi = (input) =>
  graphqlClient
    .request(manualInventoryAdjustGql, { input })
    .then(({ manualInventoryAdjust }) => manualInventoryAdjust);

export const updateInventoryReorderConfigApi = (input) =>
  graphqlClient
    .request(updateInventoryReorderConfigGql, { input })
    .then(({ updateInventoryReorderConfig }) => updateInventoryReorderConfig.inventoryItem);

export const inventoryAlertSummaryApi = ({ filter } = {}) =>
  graphqlClient
    .request(inventoryAlertSummaryGql, {
      filter: filter ? buildFilter(filter) : undefined,
    })
    .then(({ inventoryAlertSummary }) => inventoryAlertSummary);

export const listLowStockItemsApi = ({ filter, limit = 100, offset = 0 } = {}) =>
  graphqlClient
    .request(listLowStockItemsGql, {
      filter: filter ? buildFilter(filter) : undefined,
      limit,
      offset,
    })
    .then(({ listLowStockItems }) => ({
      ...listLowStockItems,
      items: (listLowStockItems.items ?? []).map(withTitleCasedDrugFields),
    }));

export const listExpiringSoonItemsApi = ({
  filter,
  withinDays = 30,
  limit = 100,
  offset = 0,
} = {}) =>
  graphqlClient
    .request(listExpiringSoonItemsGql, {
      filter: filter ? buildFilter(filter) : undefined,
      withinDays,
      limit,
      offset,
    })
    .then(({ listExpiringSoonItems }) => ({
      ...listExpiringSoonItems,
      items: (listExpiringSoonItems.items ?? []).map(withTitleCasedDrugFields),
    }));

export const listLowDaysOfSupplyItemsApi = ({
  filter,
  thresholdDays = 14,
  limit = 100,
  offset = 0,
} = {}) =>
  graphqlClient
    .request(listLowDaysOfSupplyItemsGql, {
      filter: filter ? buildFilter(filter) : undefined,
      thresholdDays,
      limit,
      offset,
    })
    .then(({ listLowDaysOfSupplyItems }) => ({
      ...listLowDaysOfSupplyItems,
      items: (listLowDaysOfSupplyItems.items ?? []).map(withTitleCasedDrugFields),
    }));

// ─── Physical Count (POM-5489) ──────────────────────────────────────────────

export const lockItemForPhysicalCountApi = (input) =>
  graphqlClient
    .request(lockItemForPhysicalCountGql, { input })
    .then(({ lockItemForPhysicalCount }) => lockItemForPhysicalCount);

export const unlockItemFromPhysicalCountApi = (input) =>
  graphqlClient
    .request(unlockItemFromPhysicalCountGql, { input })
    .then(({ unlockItemFromPhysicalCount }) => unlockItemFromPhysicalCount);

export const bulkLockItemsForPhysicalCountApi = (items) =>
  graphqlClient
    .request(bulkLockItemsForPhysicalCountGql, { items })
    .then(({ bulkLockItemsForPhysicalCount }) => bulkLockItemsForPhysicalCount);

export const bulkUnlockItemsFromPhysicalCountApi = (inventoryItemIds) =>
  graphqlClient
    .request(bulkUnlockItemsFromPhysicalCountGql, { inventoryItemIds })
    .then(({ bulkUnlockItemsFromPhysicalCount }) => bulkUnlockItemsFromPhysicalCount);

export const listInPhysicalCountItemsApi = ({ filter, limit = 25, offset = 0 } = {}) =>
  graphqlClient
    .request(listInPhysicalCountItemsGql, {
      filter: filter ? buildFilter(filter) : undefined,
      limit,
      offset,
    })
    .then(({ listInPhysicalCountItems }) => ({
      ...listInPhysicalCountItems,
      items: (listInPhysicalCountItems.items ?? []).map(withTitleCasedDrugFields),
    }));

export const createCountApi = (input) =>
  graphqlClient.request(createCountGql, { input }).then(({ createCount }) => createCount);

export const listCountsApi = ({ filter, limit = 25, offset = 0 }) =>
  graphqlClient
    .request(listCountsGql, { filter, limit, offset })
    .then(({ listCounts }) => listCounts);

export const listLockedInventoryItemsApi = ({ fulfillingPharmacyIds }) =>
  graphqlClient
    .request(listLockedInventoryItemsGql, { fulfillingPharmacyIds })
    .then(({ listLockedInventoryItems }) =>
      (listLockedInventoryItems ?? []).map(withTitleCasedDrugFields)
    );

export const createInventoryApi = (input) =>
  graphqlClient
    .request(createInventoryGql, { input })
    .then(({ createInventory }) => createInventory);

export const setDefaultNdcForProductApi = ({ productId, ndc }) =>
  graphqlClient
    .request(setDefaultNdcForProductGql, { productId, ndc })
    .then(({ setDefaultNdcForProduct }) => setDefaultNdcForProduct);
// ─── POM-5974 / POM-5973 · Partner NDC preference ─────────────────────────────

// Parent rows of the Partner Preferences tab. Only partners that already have at
// least one preference come back, so `count` is a partner count and drives the
// pagination of the outer table.
export const listPartnerNdcPreferenceCountsApi = ({
  partnerName,
  isActive = true,
  includeInactivePartners = false,
  sortOrder = 'ASC',
  limit = 25,
  offset = 0,
} = {}) =>
  graphqlClient
    .request(listPartnerNdcPreferenceCountsGql, {
      filter: buildFilter({ partnerName, isActive, includeInactivePartners }),
      sortBy: { sortOrder },
      limit,
      offset,
    })
    .then(({ listPartnerNdcPreferenceCounts }) => ({
      ...listPartnerNdcPreferenceCounts,
      result: (listPartnerNdcPreferenceCounts?.result ?? []).map((item) => ({
        ...item,
        ...(item.partnerName ? { partnerName: capitalizeName(item.partnerName) } : {}),
      })),
    }));

// Children of one expanded partner row. Always scoped by partnerUuid — the outer
// table owns pagination, so this is never called unscoped.
export const listPartnerNdcPreferencesApi = ({ partnerUuid, isActive = true } = {}) =>
  graphqlClient
    .request(listPartnerNdcPreferencesGql, {
      filter: buildFilter({ partnerUuid, isActive }),
      sortBy: { sortKey: 'drugName', sortOrder: 'ASC' },
      limit: 200,
      offset: 0,
    })
    .then(({ listPartnerNdcPreferences }) => ({
      ...listPartnerNdcPreferences,
      // Names only: withTitleCasedDrugFields would also case `strength`, and "10 Mg" is
      // the wrong symbol.
      result: (listPartnerNdcPreferences?.result ?? []).map((item) => ({
        ...item,
        ...(item.drugName ? { drugName: titleCase(item.drugName) } : {}),
        ...(item.inventoryDrugName ? { inventoryDrugName: titleCase(item.inventoryDrugName) } : {}),
        ...(item.partnerName ? { partnerName: capitalizeName(item.partnerName) } : {}),
      })),
    }));

// Resolves to the raw Boolean: true when a preference was cleared, false when
// there was nothing active to clear.
export const removePartnerNdcPreferenceApi = ({ partnerUuid, productId }) =>
  graphqlClient
    .request(removePartnerNdcPreferenceGql, { partnerUuid, productId })
    .then(({ removePartnerNdcPreference }) => removePartnerNdcPreference);

export const setPartnerNdcPreferenceApi = ({ partnerUuid, productId, ndc }) =>
  graphqlClient
    .request(setPartnerNdcPreferenceGql, { input: { partnerUuid, productId, ndc } })
    .then(({ setPartnerNdcPreference }) => setPartnerNdcPreference);
